import { useState } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { HelpDrawer } from './HelpDrawer';
import { Icon, type IconName } from './Icon';
import { useAuth } from '@/store/auth';
import { logout } from '@/api/auth';

type Module = {
  to: string;
  labelKey: string;
  icon: IconName;
  count?: number;
  /** Module réservé aux administrateurs (le serveur l'impose aussi). */
  adminOnly?: boolean;
};

const MODULES: Module[] = [
  { to: '/dossiers', labelKey: 'nav.dossiers', icon: 'folder' },
  { to: '/imports', labelKey: 'nav.imports', icon: 'upload' },
  { to: '/partners', labelKey: 'nav.partners', icon: 'plug' },
  { to: '/users', labelKey: 'nav.users', icon: 'users', adminOnly: true },
  { to: '/admin', labelKey: 'nav.admin', icon: 'shield' },
];

/**
 * Sticky left rail listing the four product modules + utility links
 * (Aide / Réglages) + the current user chip with a logout button.
 * Triggers the help drawer modally without changing route.
 */
export function Sidebar() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const user = useAuth((s) => s.user);
  const [helpOpen, setHelpOpen] = useState(false);
  const isAdmin = useAuth((st) => st.user?.roles.includes('ADMIN') ?? false);

  function onLogout() {
    // Ferme la session côté serveur aussi : sans cet appel, le jeton de
    // renouvellement restait valide jusqu'à son terme après un « quitter ».
    logout();
    void navigate({ to: '/login' });
  }

  const initials =
    (user?.displayName ?? user?.email ?? '?')
      .split(/\s+/)
      .map((p) => p.charAt(0).toUpperCase())
      .slice(0, 2)
      .join('') || '??';

  return (
    <aside className="sidebar">
      <div className="brand">
        <div className="mark">
          <Icon name="layers" />
        </div>
        <div>
          <div className="bt">PDE.CI</div>
          <div className="bs">{t('brand.subtitle')}</div>
        </div>
      </div>

      <div className="nav-group">
        <div className="nav-label">{t('nav.modules')}</div>
        {MODULES.filter((m) => !m.adminOnly || isAdmin).map((m) => (
          <Link
            key={m.to}
            to={m.to}
            className="nav-item"
            activeProps={{ className: 'nav-item active' }}
          >
            <Icon name={m.icon} />
            {t(m.labelKey)}
            {m.count != null && m.count > 0 && <span className="count">{m.count}</span>}
          </Link>
        ))}
      </div>

      <div className="spacer" />

      <div className="nav-group">
        <button type="button" className="nav-item" onClick={() => setHelpOpen(true)}>
          <Icon name="help" />
          {t('nav.help')}
        </button>
        <Link to="/settings" className="nav-item" activeProps={{ className: 'nav-item active' }}>
          <Icon name="settings" />
          {t('nav.settings')}
        </Link>
      </div>

      <div className="side-sep" />
      <div className="user-chip">
        <div className="ava">{initials}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="un">{user?.displayName || user?.email || '—'}</div>
          <div className="ue">
            {user?.source ?? ''}
            {user?.roles?.length ? ` · ${user.roles.join(', ')}` : ''}
          </div>
        </div>
        <button
          className="iconbtn"
          aria-label={t('auth.logout') ?? 'Se déconnecter'}
          title={t('auth.logout') ?? ''}
          style={{
            width: 32,
            height: 32,
            border: 'none',
            boxShadow: 'none',
            background: 'none',
          }}
          onClick={onLogout}
        >
          <Icon name="logout" size={16} />
        </button>
      </div>

      <HelpDrawer opened={helpOpen} onClose={() => setHelpOpen(false)} />
    </aside>
  );
}
